import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaFileContract, FaGavel, FaHandshake, FaLock, FaShieldAlt, FaBalanceScale, FaEnvelope, FaPhone, FaMapMarkerAlt } from "react-icons/fa";
import ThemeToggle from "../../components/ThemeToggle";

export default function TermsOfService({ theme, setTheme }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300 relative">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-4 left-4 flex items-center gap-2 px-4 py-2 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all duration-200 shadow-sm border border-gray-200 dark:border-gray-700 z-10"
      >
        <FaArrowLeft className="w-5 h-5" />
        <span className="font-medium">Back</span>
      </button>

      {/* Theme Toggle */}
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle theme={theme} setTheme={setTheme} />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="mb-12 pt-16">
          <div className="bg-indigo-900 dark:bg-indigo-950 rounded-lg p-8 md:p-12 shadow-lg border border-indigo-800 dark:border-indigo-900">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-indigo-800 dark:bg-indigo-900 rounded-lg">
                <FaFileContract className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-white">
                  Terms of Use
                </h1>
                <p className="text-indigo-100 text-sm mt-2">
                  Effective Date: November 1, 2025
                </p>
              </div>
            </div>
            <p className="text-indigo-100 text-lg leading-relaxed max-w-3xl">
              These Terms of Use ("Agreement") govern your access to and use of the
              SecurechatAI service (the "Service") made available by Spa City
              Partners LLC ("we," "us," "our"). By accessing or using the Service,
              you agree to be bound by this Agreement.
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-lg dark:prose-invert max-w-none">

          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {[
              { num: "1", title: "Term", icon: FaFileContract },
              { num: "2", title: "Use of the Service", icon: FaHandshake },
              { num: "3", title: "Service Availability", icon: FaShieldAlt },
              { num: "4", title: "Data Protection and Privacy", icon: FaLock },
              { num: "5", title: "Proprietary Rights", icon: FaFileContract },
              { num: "6", title: "User Content", icon: FaFileContract },
              { num: "7", title: "Fees and Payment", icon: FaFileContract },
              { num: "8", title: "Confidentiality", icon: FaLock },
              { num: "9", title: "Indemnification", icon: FaShieldAlt },
              { num: "10", title: "Limitation of Liability", icon: FaBalanceScale },
              { num: "11", title: "Termination", icon: FaFileContract },
              { num: "12", title: "Force Majeure", icon: FaFileContract },
              { num: "13", title: "Governing Law", icon: FaGavel },
              { num: "14", title: "Entire Agreement", icon: FaFileContract },
              { num: "15", title: "Severability", icon: FaFileContract },
              { num: "16", title: "Waiver", icon: FaFileContract },
              { num: "17", title: "Assignment", icon: FaFileContract },
              { num: "18", title: "No Partnership", icon: FaHandshake },
              { num: "19", title: "Notices", icon: FaEnvelope },
            ].map(({ num, title, icon: Icon }) => (
              <section
                key={num}
                className="p-5 rounded-lg bg-gray-50 dark:bg-gray-800 border-l-4 border-indigo-600 shadow-sm hover:shadow transition-all duration-200"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-indigo-600 rounded-lg">
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {num}. {title}
                  </h2>
                </div>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-sm">
                  {num === "1" && "This Agreement begins when you first access or use the Service and continues until terminated as set out herein."}
                  {num === "2" && "You receive a limited, non-exclusive, non-transferable, revocable license to use the Service for internal business purposes."}
                  {num === "3" && "We use commercially reasonable efforts to maintain availability but may suspend or modify the Service at any time."}
                  {num === "4" && "You must comply with all data protection laws. We collect only aggregated usage data unless otherwise stated."}
                  {num === "5" && "All intellectual property related to the Service belongs to Spa City Partners LLC or its licensors."}
                  {num === "6" && "You retain ownership of content you upload. You grant us a license to use it only as necessary to provide the Service."}
                  {num === "7" && "If applicable, you agree to pay all fees. Fees are non-refundable unless otherwise stated."}
                  {num === "8" && "Both parties agree to keep each other's confidential information secure and not disclose it without permission."}
                  {num === "9" && "You agree to indemnify Spa City Partners LLC against claims arising from your use of the Service or violation of this Agreement."}
                  {num === "10" && "Our liability is limited to the amount paid in the preceding period. We are not liable for indirect or consequential damages."}
                  {num === "11" && "Either party may terminate with notice. Upon termination, access ceases and we may delete your data."}
                  {num === "12" && "Neither party is liable for delays due to events beyond reasonable control."}
                  {num === "13" && "This Agreement is governed by the laws of the State of New York, United States."}
                  {num === "14" && "This Agreement supersedes all prior agreements."}
                  {num === "15" && "Invalid provisions do not affect the remainder of the Agreement."}
                  {num === "16" && "A failure to exercise a right is not a waiver of that right."}
                  {num === "17" && "You may not assign this Agreement; we may assign with notice."}
                  {num === "18" && "This Agreement does not create a partnership or joint venture."}
                  {num === "19" && "Notices will be sent to the emails specified."}
                </p>
              </section>
            ))}
          </div>

          <section className="mb-8 p-6 rounded-lg bg-indigo-900 dark:bg-indigo-950 shadow-lg border border-indigo-800 dark:border-indigo-900">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-indigo-800 dark:bg-indigo-900 rounded-lg">
                <FaEnvelope className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-semibold text-white">Contact</h2>
            </div>
            <div className="text-white space-y-4">
              <div className="flex items-start gap-3">
                <FaMapMarkerAlt className="w-5 h-5 mt-1 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-lg">Spa City Partners LLC</p>
                  <p>475 Broadway, Box 1217, Saratoga Springs, NY 12866</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <FaEnvelope className="w-5 h-5 flex-shrink-0" />
                <a
                  href="mailto:app@newlogiq.com"
                  className="hover:text-indigo-200 font-semibold underline decoration-2 underline-offset-2 transition-colors"
                >
                  app@newlogiq.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <FaPhone className="w-5 h-5 flex-shrink-0" />
                <a
                  href="tel:5184289229"
                  className="hover:text-indigo-200 font-semibold underline decoration-2 underline-offset-2 transition-colors"
                >
                  518.428.9229
                </a>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

